import { useDateFormat } from '@/hooks/useDateFormat';
import {
  WorkplaceRequestEvent,
  WorkplaceRequestEventAction,
  WorkplaceRequestEventUser
} from '@gettactic/api';
import { UserAvatar } from '@v2/components/display/UserAvatar/UserAvatar';
import { formatDistanceToNow } from 'date-fns';


const ACTION_DESCRIPTIONS: Record<WorkplaceRequestEventAction, string> = {
  status_changed: 'Status changed',
  priority_changed: 'Priority changed',
  type_changed: 'Request type changed',
  office_changed: 'Office changed',
  resource_changed: 'Resource changed',
  owner_changed: 'Owner changed',
  description_changed: 'Description changed',
  title_changed: 'Title changed'
};

interface RequestEventsListProps {
  events: WorkplaceRequestEvent[];
  isLoading?: boolean;
  users?: Record<string, WorkplaceRequestEventUser>;
}

export function RequestEventsList({
  events,
  isLoading,
  users = {}
}: RequestEventsListProps) {
  const formatDate = useDateFormat();

  if (isLoading) {
    return (
      <div className="space-y-4 text-muted-foreground text-sm">
        Loading events...
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="space-y-4 text-muted-foreground text-sm">
        No events yet
      </div>
    );
  }

  const getUserName = (userId: string): string => {
    const user = users[userId];
    return user ? user.name : userId;
  };

  const formatActionDescription = (event: WorkplaceRequestEvent): string => {
    const baseDescription = ACTION_DESCRIPTIONS[event.action];

    
    if (event.action === 'status_changed' && event.data.from && event.data.to) {
      return `${baseDescription} from ${event.data.from} to ${event.data.to}`;
    }

    if (event.action === 'owner_changed' && event.data.from && event.data.to) {
      const fromUserName = getUserName(event.data.from);
      const toUserName = getUserName(event.data.to);
      return `${baseDescription} from ${fromUserName} to ${toUserName}`;
    }

    return baseDescription;
  };

  return (
    <div className="space-y-3">
      {events.map((event) => (
        <div key={event.id} className="flex items-start space-x-3">
          <UserAvatar
            user={{
              name: event.user.name,
              email: event.user.email,
              profile_photo_url: event.user.profile_photo_url
            }}
            size="xs"
            className="mt-0.5 flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center space-x-2">
              <span className="truncate font-inter font-medium text-[12px] text-gray-900">
                {event.user.name}
              </span>
              <span className="text-[10px] text-gray-400">•</span>
              <span className="text-[10px] text-gray-500">
                {formatDistanceToNow(new Date(event.created), {
                  addSuffix: true
                })}
              </span>
            </div>
            <p className="text-[12px] text-gray-600">
              {formatActionDescription(event)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
